/**
 * Form-field options normalization.
 *
 * Forms in this codebase have stored field options in two shapes:
 *   - string[]           — e.g. ["Owned", "Rented"]
 *   - {label, value}[]   — e.g. [{label: "Owned", value: "owned"}]
 *
 * Renderers (FormInstanceEditor, PublicFormPage, FormBuilder preview) must
 * tolerate both shapes. This helper returns the canonical object shape so
 * callers can render uniformly.
 */

export interface NormalizedOption {
  label: string;
  value: string;
}

export type RawOption = string | { label?: string; value?: string };

export function normalizeOptions(
  options: ReadonlyArray<RawOption> | null | undefined
): NormalizedOption[] {
  if (!options || options.length === 0) return [];
  return options
    .map((opt): NormalizedOption | null => {
      if (typeof opt === 'string') {
        const trimmed = opt.trim();
        if (!trimmed) return null;
        return { label: trimmed, value: trimmed };
      }
      if (opt && typeof opt === 'object') {
        const label = (opt.label ?? opt.value ?? '').toString().trim();
        const value = (opt.value ?? opt.label ?? '').toString().trim();
        if (!label && !value) return null;
        return { label: label || value, value: value || label };
      }
      return null;
    })
    .filter((opt): opt is NormalizedOption => opt !== null);
}

/**
 * Field types that require an options list to render correctly.
 * Used both by renderers (to guard rendering) and by the form-builder save
 * path (to reject saving a `text` field with non-empty options).
 */
export const OPTION_BEARING_FIELD_TYPES = new Set([
  'select',
  'radio',
  'checkbox',
  'multi_select',
]);

export function fieldTypeRequiresOptions(fieldType: string | undefined): boolean {
  return !!fieldType && OPTION_BEARING_FIELD_TYPES.has(fieldType);
}

/**
 * Scalar field types that cannot honor options. If a field is saved with
 * non-empty options while declared as one of these, the renderer drops the
 * options entirely. The save-path guard coerces these to `select`.
 */
const SCALAR_FIELD_TYPES = new Set(['text', 'number', 'textarea', 'email', 'phone']);

/**
 * If `options` is non-empty but `fieldType` is a scalar that ignores options,
 * return `select` so the renderer can display the choices. Otherwise return
 * the original type unchanged. Prevents the recurring bug where a field is
 * imported from member field config with options but its type was never
 * updated past the default `text`.
 */
export function coerceFieldTypeForOptions(
  fieldType: string | undefined,
  options: ReadonlyArray<RawOption> | null | undefined
): string {
  const original = fieldType || 'text';
  const hasOptions = !!options && normalizeOptions(options).length > 0;
  if (hasOptions && SCALAR_FIELD_TYPES.has(original)) {
    return 'select';
  }
  return original;
}

